// =============================================================================
// ui/state/share.tsx — the header's Share button, and what it publishes.
// -----------------------------------------------------------------------------
// The same seam as `snapshot.tsx`, for the same reason: the button lives in the
// toolbar and the report it publishes lives in a TAB. A tab opts in by
// registering a source; the shell asks.
//
// ⚠ A SEPARATE CONTEXT FROM THE CAMERA, deliberately. They act on the same three
// tabs and they are not the same act: a snapshot is a picture that stays on the
// sharer's machine; a share is a URL that anyone holding it can open. The second
// needs a set of trades, an expiry, a token and a revoke list, none of which a
// snapshot has any use for — and folding them together would put the disclosure
// decisions behind a type whose name says "camera".
//
// ── 🚨 THE PROVIDER TAKES `create` AS A PROP, AND THAT IS NOT PLUMBING ──
//
// Publishing a link needs five contexts — the client, preferences, accounts,
// patterns, exit models. If the provider read them itself, every tab that
// merely REGISTERS a source would have to be mounted inside all five, in every
// test, for a feature none of those tests exercise. That is how one provider
// takes sixty tests with it.
//
// So the registration half has no dependencies at all, and the publishing half
// arrives as a function. `ShareHost` is the one component that assembles both,
// and it is mounted where the five contexts already exist.
//
// ── 🚨 THE REGION IS THE WHOLE TAB, NOT THE SNAPSHOT REGION ──
//
// Beta captures `.tab-section[data-tab="<kind>"]` minus `[data-noshare]`
// (app/index.html:29392). That is WIDER than the camera's region and NARROWER
// than the screen: it takes the scorecard and the breakdowns, and it drops the
// four things D213 removed — the behavioural rule chip, the prop rails card with
// the daily loss limit in dollars, the by-account roster and the plan/recap
// prose.
// =============================================================================

import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react';
import {
  buildSharePayload, copyShotsIntoPayload, createShare, loadTradesForShare,
  newShareToken, shareExpiresAt, shareLinkFor,
  type ShareKind,
} from '../../selectors/share';
import type { PnlUnit } from '../../selectors/pnlUnit';
import { parseTagRegistry } from '../../selectors/accounts';
import { TRADE_BOOLEANS } from '../../selectors/tradeForm';
import { downloadMedia } from '../../io/storage/media';
import { reportFailureAs } from '../../io/diagnostics/errorLog';
import { useSupabase } from './supabase';
import { usePreferences } from './preferences';
import { useAccounts } from './accounts';
import { usePatterns } from './patterns';
import { useExitModels } from './exitModels';
import { ShareCreated, ShareDialog, type ShareChoice } from '../share/ShareDialog';

/** What a shareable tab tells the shell about itself. */
export interface ShareSource {
  kind: ShareKind;
  /** "Dashboard" / "Day Overview" / "Calendar". */
  title: string;
  /** The period: a date range, a long-form date, a month. */
  subtitle: string;
  /** The active-account label for the public header. Masked before it is stored. */
  accountLabel: string;
  /**
   * ⚠ THE TRADES THE REPORT LISTS, IN THE ORDER IT LISTS THEM.
   *
   * A disclosure decision, not a convenience: everything named here is
   * serialised into a payload that is readable whether or not it is drawn. A tab
   * that passed its whole book would publish trades its own report never
   * mentions.
   */
  tradeIds: readonly string[];
  /** The sharer's display unit. The whole report is in it, table included. */
  unit: PnlUnit;
  /**
   * Each listed trade's value in that unit, by id.
   *
   * ⚠ THE TAB CONVERTS, because it owns the contract catalogue — ticks need a
   * tick size, R needs a risk basis, and a visitor has neither. A report shared
   * in points whose table printed dollars is two different claims on one page,
   * which is what the first version did.
   */
  valueById: Readonly<Record<string, number | null>>;
}

export interface CreatedLink {
  link: string;
  /** False when the clipboard write was refused — then the field is the only copy. */
  copied: boolean;
}

/** How a link actually gets made. Injected, so registration needs no contexts. */
export type ShareCreator = (
  source: ShareSource,
  choice: ShareChoice,
  progress: (status: string) => void,
) => Promise<CreatedLink>;

export interface ShareState {
  /** Null when the active tab cannot be shared — the button hides itself. */
  source: ShareSource | null;
  setSource: (s: ShareSource | null) => void;
  /** Opens the options dialog. ⚠ Nothing is written until it is submitted. */
  begin: () => void;
  open: boolean;
}

const Ctx = createContext<ShareState | null>(null);

export function useShare(): ShareState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useShare() outside <ShareProvider>');
  return v;
}

type Phase =
  | { kind: 'closed' }
  | { kind: 'options'; busy: boolean; status: string | null; error: string | null }
  | { kind: 'created'; link: string; expiryLabel: string | null; copied: boolean };

export function ShareProvider(
  { create, children }: { create?: ShareCreator; children: ReactNode },
) {
  const [source, setSource] = useState<ShareSource | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'closed' });

  const begin = useCallback(() => {
    setPhase({ kind: 'options', busy: false, status: null, error: null });
  }, []);

  const run = useCallback((choice: ShareChoice) => {
    if (!source || !create) return;
    setPhase({ kind: 'options', busy: true, status: 'Freezing the report…', error: null });
    void (async () => {
      try {
        const { link, copied } = await create(source, choice, (status) => {
          setPhase({ kind: 'options', busy: true, status, error: null });
        });
        setPhase({
          kind: 'created', link, copied,
          expiryLabel: choice.hours ? choice.label : null,
        });
      } catch (e) {
        setPhase({
          kind: 'options', busy: false, status: null,
          // ⚠ Through the funnel, so a failed share reaches the activity log
          // rather than living only in a modal the user is about to close.
          error: reportFailureAs(
            'share', e,
            e instanceof Error && e.message
              ? e.message : 'the share link could not be created',
          ),
        });
      }
    })();
  }, [source, create]);

  const value = useMemo<ShareState>(
    () => ({ source, setSource, begin, open: phase.kind !== 'closed' }),
    [source, begin, phase.kind],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      {phase.kind === 'options' && source ? (
        <ShareDialog
          what={`${source.title}${source.subtitle ? ` — ${source.subtitle}` : ''}`}
          tradeCount={source.tradeIds.length}
          busy={phase.busy}
          status={phase.status}
          error={phase.error}
          onCreate={run}
          onCancel={() => setPhase({ kind: 'closed' })}
        />
      ) : null}
      {phase.kind === 'created' ? (
        <ShareCreated
          link={phase.link}
          expiryLabel={phase.expiryLabel}
          copied={phase.copied}
          onClose={() => setPhase({ kind: 'closed' })}
        />
      ) : null}
    </Ctx.Provider>
  );
}

/**
 * The publishing half: read → serialise → store → hand back a URL.
 *
 * ⚠ THE REPORT IS READ FROM THE LIVE DOCUMENT AT THE MOMENT THE SHARER
 * CONFIRMS, not when the dialog opened. Between those two the sharer can scroll,
 * change the account filter or page to another day, and publishing the earlier
 * screen would hand out a report they never looked at.
 */
export function useShareCreator(): ShareCreator {
  const { sb, userId } = useSupabase();
  const { prefs } = usePreferences();
  const { accounts } = useAccounts();
  const { patterns } = usePatterns();
  const { exitModels } = useExitModels();

  return useCallback<ShareCreator>(async (source, choice, progress) => {
    const region = document.querySelector(`.tab-section[data-tab="${source.kind}"]`);
    if (!region) throw new Error('the report is no longer on screen');

    const rows = await loadTradesForShare(sb, source.tradeIds);

    const { payload, trades } = buildSharePayload({
      region,
      kind: source.kind,
      title: source.title,
      subtitle: source.subtitle,
      accountLabel: source.accountLabel,
      rows,
      mode: prefs?.dayBoundaryMode ?? 'calendar',
      unit: source.unit,
      valueById: source.valueById,
      registries: {
        patterns: patterns ?? [],
        exitModels: exitModels ?? [],
        tags: parseTagRegistry(prefs?.customTags),
        flags: TRADE_BOOLEANS,
        accounts: (accounts ?? []).map((a) => ({
          id: String(a.id), name: a.name, externalId: a.externalId,
        })),
      },
      opts: { notes: choice.notes, shots: choice.shots },
      now: Date.now(),
    });

    if (choice.shots && trades.length) {
      progress('Copying screenshots…');
      const r = await copyShotsIntoPayload(
        rows, trades, (f) => downloadMedia(sb, 'screenshots', f),
      );
      // ⚠ SAID OUT LOUD. A silently capped share looks complete and is not.
      if (r.skipped) {
        progress(`${r.copied} screenshot(s) included, ${r.skipped} left out (per-share limit).`);
      }
    }

    progress('Storing the report…');
    const token = newShareToken();
    await createShare(sb, {
      token, userId, kind: source.kind, payload,
      expiresAt: shareExpiresAt(choice.hours, Date.now()),
    });

    const link = shareLinkFor(token, window.location);
    let copied = false;
    try {
      await navigator.clipboard.writeText(link);
      copied = true;
    } catch {
      // ⚠ NOT A FAILURE. The clipboard is refused in plenty of ordinary
      // situations, and the link exists either way — the modal says which.
      copied = false;
    }
    return { link, copied };
  }, [sb, userId, prefs, accounts, patterns, exitModels]);
}

/**
 * Provider + creator, assembled where the five contexts it needs already exist.
 *
 * ⚠ This is the only place that knows both halves. Everything else either
 * registers a source (no dependencies) or presses a button (no dependencies).
 */
export function ShareHost({ children }: { children: ReactNode }) {
  const create = useShareCreator();
  return <ShareProvider create={create}>{children}</ShareProvider>;
}
